import type { Prettify } from '@mnd-frontend/ts';
import type { MouseEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { SPACING_1, SPACING_2 } from '../../theme';
import { Button } from '../Button';
import Icons from '../Icons';
import { Text } from '../Text';
import { breakpoints } from '../utils/media';

type AlertProps = Prettify<CommonProps & (WithoutIconProps | IconProps | SmartAlertProps)>;

type ActionButton = {
  text: string;
  onClick: (e: MouseEvent) => void;
  href?: undefined;
  loading?: boolean;
};
type ActionAnchor = {
  text: string;
  href: string;
  onClick?: (event: MouseEvent) => void;
  loading?: boolean;
};

type ActionProp = Prettify<ActionButton | ActionAnchor>;

type CommonProps = {
  children: ReactNode;
  action?: ActionProp;
  title?: string;
};

interface WithoutIconProps {
  type: 'info' | 'warning' | 'error';
  action?: ActionProp;
}

interface IconProps {
  type?: 'default';
  icon?: ReactNode;
}

type SmartAlertProps = Prettify<{ type: 'smart' } & (SmartAlertBar | SmartAlertBox)>;

type SmartAlertBar = {
  variant?: 'bar';
  action?: ActionProp;
};

type SmartAlertBox = {
  variant: 'box';
  action: ActionProp;
};

type BreakpointPixcel = `${(typeof breakpoints)[keyof typeof breakpoints]}px`;
const breakpointTablet: BreakpointPixcel = `${breakpoints.tablet}px`;

const BoldText = styled(Text)`
  font-weight: 600;
`;

const IconWrapper = styled.div`
  > * {
    width: ${props => props.theme.sizingIconXs};
    height: ${props => props.theme.sizingIconXs};
    flex-grow: 1;
  }
`;

const WRAPPER_BASE_STYLE = styled.aside`
  text-align: left;
  color: ${props => props.theme.colorTextBodyPrimary};
  border-radius: ${props => props.theme.radiusAlert};

  > p {
    font-size: 0.875rem;

    @media (min-width: ${breakpointTablet}) {
      font-size: 1rem;
    }
  }
`;

const Wrapper = styled(WRAPPER_BASE_STYLE)<{
  $type: AlertProps['type'];
  $hasButton: boolean;
}>`
  display: grid;
  column-gap: ${SPACING_2};
  align-items: center;
  min-height: ${props => props.theme.sizingAlertDefault};
  padding: ${SPACING_2};

  ${props =>
    props.$hasButton
      ? css`
          grid-template-rows: auto 3rem;
          grid-template-columns: 1.5rem 1fr;

          > button,
          > a {
            grid-column: 1 / 3;
          }

          @media (min-width: ${breakpointTablet}) {
            grid-template-rows: 1fr;
            grid-template-columns: 1.5rem 1fr auto;

            > button,
            > a {
              grid-row: 1;
              grid-column: 3 / 4;
            }
          }
        `
      : css`grid-template-columns: auto 1fr auto;`}

  ${props => {
    switch (props.$type) {
      case 'error':
        return css`
          background: ${props.theme.colorBackgroundAlertError};
          ${IconWrapper} {
            color: ${props.theme.colorIconsDanger};
          }
        `;
      case 'info':
        return css`
          background: ${props.theme.colorBackgroundAlertInfo};
          ${IconWrapper} {
            color: ${props.theme.colorIconsInfo};
          }
        `;
      case 'smart':
        return css`
          background: ${props.theme.colorBackgroundAlertSmart};
          ${IconWrapper} {
            color: ${props.theme.colorIconsSmart};
          }
        `;
      case 'warning':
        return css`
          background: ${props.theme.colorBackgroundAlertWarning};
          ${IconWrapper} {
            color: ${props.theme.colorIconsWarning};
          }
        `;
      default:
        return css`
          background: ${props.theme.colorBackgroundAlertDefault};
          ${IconWrapper} {
            color: ${props.theme.colorIconsDefault};
          }
        `;
    }
  }}
`;

const WrapperBox = styled(WRAPPER_BASE_STYLE)`
  display: flex;
  flex-direction: column;
  gap: ${SPACING_1};
  background: ${props => props.theme.colorBackgroundAlertSmart};
  padding: ${SPACING_2};
  max-width: 30rem;

  .footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${SPACING_2};
    
    button {
      text-wrap: wrap;
      text-align: left;
      max-width: 85%;
    }

    svg {
      color: ${props => props.theme.colorIconsSmart};
    }
  }
`;

const AlertText = ({ title, children }: { children: ReactNode; title?: string }) => (
  <Text as="p" variant="body">
    {title && (
      <strong>
        <BoldText as="span" variant="body">
          {title}{' '}
        </BoldText>
      </strong>
    )}
    {children}
  </Text>
);

const isAnchorAction = (action: ActionProp): action is ActionAnchor => {
  return typeof action.href === 'string';
};

const AlertButton = ({ action }: { action: ActionProp }) => {
  if (!action) return null;
  return (
    <Button
      configuration="text"
      {...(isAnchorAction(action) ? { ...action } : { ...action, type: 'button' })}
    >
      {action.text}
    </Button>
  );
};

export const Alert = ({ type, action, children, title, ...props }: AlertProps) => {
  const { t } = useTranslation();

  if ('variant' in props && props.variant === 'box') {
    return (
      <WrapperBox>
        <AlertText title={title}>{children}</AlertText>
        <div className="footer">
          <AlertButton action={action as NonNullable<ActionProp>} />
          <Icons.Sparkle aria-label={t('common_ai_suggestion')} />
        </div>
      </WrapperBox>
    );
  }

  let icon = null;
  if ('icon' in props) {
    icon = props.icon ?? null;
  } else if (type === 'error') {
    icon = <Icons.Error aria-label={t('common_error')} />;
  } else if (type === 'info') {
    icon = <Icons.CircleInfo />;
  } else if (type === 'warning') {
    icon = <Icons.Warning aria-label={t('common_warning')} />;
  } else if (type === 'smart') {
    icon = <Icons.Sparkle aria-label={t('common_ai_suggestion')} />;
  }

  return (
    <Wrapper $type={type} $hasButton={!!action}>
      {icon && <IconWrapper>{icon}</IconWrapper>}
      <AlertText title={title}>{children}</AlertText>
      {action && <AlertButton action={action} />}
    </Wrapper>
  );
};
